<template>
  <div class="pl-agenda">
    <div class="row">
      <!-- Main column first in the DOM so mobile leads with the agenda; pushed
           right of the sidebar on desktop -->
      <div :class="compact ? 'col-xs-12' : 'col-md-9 col-md-push-3'">
        <!-- Stats over the next 7 days -->
        <div v-if="!compact" class="pl-agenda-stats flex m-b-sm">
          <div class="pl-agenda-stat">
            <p class="m-b-none text-12 text-gray-600 pl-truncate">Open next 7 days</p>
            <p class="m-b-none text-20 font-heavy pl-slot-time">{{ stats.open }}</p>
          </div>
          <div class="pl-agenda-stat">
            <p class="m-b-none text-12 text-gray-600 pl-truncate">Booked</p>
            <p class="m-b-none text-20 font-heavy pl-slot-time">{{ stats.booked }}</p>
          </div>
          <div class="pl-agenda-stat">
            <p class="m-b-none text-12 text-gray-600 pl-truncate">Needs confirmation</p>
            <p class="m-b-none text-20 font-heavy pl-slot-time">{{ stats.invited }}</p>
          </div>
        </div>

        <date-strip-picker
          v-model="selectedDate"
          :active-dates="activeDates"
          title=""
          :loading-more="loadingMore"
          :allow-past="true"
          :loading-earlier="loadingEarlier"
          @load-more="loadMore"
          @load-earlier="loadEarlier"
        ></date-strip-picker>

        <!-- Compact mode trades the stats trio for a glanceable one-liner -->
        <p v-if="compact" class="text-12 text-gray-500 text-center m-t-xs m-b-sm">
          Next 7 days:
          <span class="font-semi text-gray-700">{{ stats.open }} open</span> ·
          <span class="font-semi text-gray-700">{{ stats.booked }} booked</span>
        </p>

        <!-- All slots vs actual bookings (confirmed + needs confirmation).
             Purely client-side — the whole range is already loaded. -->
        <div class="flex justify-center m-t-xs m-b-sm">
          <div class="btn-group segmented-control" role="group" aria-label="Filter slots">
            <button type="button" class="btn btn-default btn-compact" :class="{ active: statusFilter === 'all' }" data-cy="agenda-filter-all" @click="statusFilter = 'all'">All slots</button>
            <button type="button" class="btn btn-default btn-compact" :class="{ active: statusFilter === 'booked' }" data-cy="agenda-filter-booked" @click="statusFilter = 'booked'">Booked</button>
          </div>
        </div>

        <div v-if="loading" class="text-center p-lg">
          <i class="fa-solid fa-spinner fa-spin text-gray-500"></i>
        </div>
        <template v-else>
          <div v-if="selectedDayEmpty" class="bg-gray-50 rounded-sm p-md text-center m-b-md">
            <p v-if="bookedOnly" class="text-gray-600 m-b-xs">No booked lessons on {{ selectedDate.format('dddd, MMM D') }}.</p>
            <p v-else class="text-gray-600 m-b-xs">No availability on {{ selectedDate.format('dddd, MMM D') }}.</p>
            <div class="flex justify-center align-items-center gap-sm flex-wrap">
              <button v-if="bookedOnly" type="button" class="btn btn-link text-blue-600" data-cy="agenda-show-all-slots" @click="statusFilter = 'all'">Show all slots</button>
              <a v-else-if="!selectedDateInPast" :href="forAppEmbed ? undefined : addHrefForSelectedDate" class="btn btn-default" @click="handleAddClick">Add hours for {{ selectedDate.format('dddd') }}</a>
              <button v-if="nextOpenDate" type="button" class="btn btn-link text-blue-600" data-cy="agenda-next-open-day" @click="selectedDate = nextOpenDate">
                Next day with lessons: {{ nextOpenDate.format('ddd, MMM D') }} &rarr;
              </button>
            </div>
          </div>

          <div v-if="dayGroups.length === 0" class="text-center p-lg">
            <template v-if="bookedOnly">
              <p class="text-gray-600 m-b-xs">No booked lessons yet.</p>
              <button type="button" class="btn btn-link text-blue-600" data-cy="agenda-show-all-slots" @click="statusFilter = 'all'">Show all slots</button>
            </template>
            <template v-else>
              <p class="text-gray-600 m-b-xs">No upcoming availability yet.</p>
              <!-- Compact mode is the coach's own home screen; the full page is
                   also read by admins managing the coach -->
              <p class="text-gray-500 text-14">Create your first open slots and wrestlers will be able to book private lessons with {{ compact ? 'you' : coach.first_name }}.</p>
            </template>
          </div>

          <!-- Only the selected day renders — the strip picks the day and the
               segmented control narrows by status -->
          <div v-if="selectedDayGroup" class="m-b-md">
            <h3 class="text-16 font-semi m-t-sm m-b-none">
              {{ selectedDayGroup.title }}
              <span class="text-gray-500 font-normal text-14">{{ selectedDayGroup.subtitle }}</span>
            </h3>
            <!-- Matching space between the heading and the first row's border,
                 same treatment as the command center's coach groups -->
            <div class="m-t-m">
              <availability-slot-row
                v-for="lessonSlot in selectedDayGroup.slots"
                :key="lessonSlot.id"
                :lesson-slot="lessonSlot"
                :confirming-delete-slot-id="confirmingDeleteSlotId"
                :show-lesson-actions="showLessonActions"
                :for-app-embed="forAppEmbed"
                @delete="confirmDelete"
                @update-status="updateBookingStatus"
              ></availability-slot-row>
            </div>
          </div>
        </template>

        <div v-show="error" class="m-t-sm">
          <small><span class="text-danger">{{ errorMessage }}</span></small>
        </div>
      </div>

      <div v-if="!compact" class="col-md-3 col-md-pull-9 hidden-print">
        <slot name="sidebar"></slot>
        <hr/>

        <a :href="addHrefForSelectedDate" class="btn btn-primary w-full hidden-xs hidden-sm m-b-sm" data-cy="agenda-add-availability">
          <i class="fa-solid fa-plus m-r-xs"></i> Add Availability
        </a>

        <a href="javascript:void(0)" onclick="Beacon('article', '6846098ca0fc382125f8525e', { type: 'modal' })" class="btn btn-default w-full hidden-xs hidden-sm justify-center flex align-items-center gap-x-xs m-b-sm">
          <i class="fa-solid fa-play"></i> Watch Overview Video
        </a>

        <!-- Host-provided extras (e.g. the per-coach earnings summary on the
             admin drill-down) sit between the standing CTAs and bulk close -->
        <slot name="sidebar-tools"></slot>

        <!-- Bulk close: the escape hatch for "I created recurring slots way too
             far into the future". Open slots only; booked lessons are untouched. -->
        <div class="m-t-md">
          <h4 class="font-semi m-b-xs">Close open slots</h4>
          <close-open-slots :coach="coach" @closed="refreshAll"></close-open-slots>
        </div>
      </div>
    </div>

    <!-- Mobile: thumb-reachable create action -->
    <div v-if="!compact" class="pl-agenda-addbar visible-xs visible-sm">
      <a :href="addHrefForSelectedDate" class="btn btn-primary w-full">
        <i class="fa-solid fa-plus m-r-xs"></i> Add Availability
      </a>
    </div>
  </div>
</template>

<script>
import qs from 'qs';
import DateStripPicker from './date_strip_picker.vue';
import AvailabilitySlotRow from './availability_slot_row.vue';
import CloseOpenSlots from './close_open_slots.vue';
import { slotFromEvent } from './private_lesson_slot_mapper';
import { errorableMix } from './mixins/errorable_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import { privateLessonBookingMix } from './mixins/private_lesson_booking_mix';

// Mobile-first coach availability agenda: replaces the FullCalendar timeline on
// the coach-specific private lesson dashboard. Shows a date strip over a
// day-grouped list of availability slots and hosts the bulk "close open slots
// after a date" tool. Creation lives on its own wizard page (availability_wizard).
export default {
  name: 'coach-availability-agenda',
  components: {
    DateStripPicker,
    AvailabilitySlotRow,
    CloseOpenSlots,
  },
  mixins: [errorableMix, appEmbedMix, privateLessonBookingMix],
  props: {
    coach: {
      type: Object,
      required: true,
    },
    newAvailabilityPath: {
      type: String,
      required: true,
    },
    // Coach-home mode: single full-width column, no sidebar/bulk-close/stats
    // trio (a one-liner takes their place). The host renders its own CTAs.
    compact: {
      type: Boolean,
      default: false,
    },
    // Render confirm/decline/checkout/book affordances on slot rows.
    // (forAppEmbed comes from appEmbedMix.)
    showLessonActions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    // ?date=YYYY-MM-DD (e.g. from the availability wizard's redirect) lands
    // the agenda on that day instead of today
    const dateParam = new URLSearchParams(window.location.search).get('date');
    const parsedDate = dateParam ? moment(dateParam, 'YYYY-MM-DD', true) : null;
    const selectedDate = parsedDate && parsedDate.isValid() ? parsedDate : moment();
    const defaultThrough = moment().add(30, 'days').endOf('day');
    return {
      loading: true,
      loadingMore: false,
      loadingEarlier: false,
      events: [],
      selectedDate,
      // The strip starts with 30 visible days; keep our fetch window in sync.
      // A ?date beyond the default window stretches the initial fetch to it.
      loadedThrough: selectedDate.clone().endOf('day').isAfter(defaultThrough) ? selectedDate.clone().endOf('day') : defaultThrough,
      // How far back we've fetched; walked backwards by the strip's "Earlier"
      loadedBackTo: moment().startOf('day'),
      confirmingDeleteSlotId: null,
      // 'all' | 'booked' — booked narrows to confirmed + needs-confirmation
      statusFilter: 'all',
    };
  },
  computed: {
    // Raw events -> display slots, sorted by start time. Cancelled/declined
    // slots stay in the list — the agenda is the coach's own record.
    slots() {
      const vm = this;
      return vm.events
        .filter((event) => event.event_type === 'private_lesson')
        .map((event) => slotFromEvent(event, { coachId: vm.coach.id }))
        .filter((lessonSlot) => lessonSlot !== null)
        .sort((a, b) => a.startAt - b.startAt);
    },
    bookedOnly() {
      return this.statusFilter === 'booked';
    },
    // Day grouping, strip dimming, and next-day jumps all derive from this, so
    // the whole agenda follows the segmented control automatically. Stats stay
    // on the unfiltered slots — they're already a status breakdown.
    filteredSlots() {
      if (!this.bookedOnly) {
        return this.slots;
      }
      return this.slots.filter((lessonSlot) => ['confirmed', 'invited'].includes(lessonSlot.status));
    },
    dayGroups() {
      const groups = [];
      const byKey = {};
      this.filteredSlots.forEach((lessonSlot) => {
        const key = lessonSlot.startAt.format('YYYY-MM-DD');
        if (!byKey[key]) {
          byKey[key] = {
            key,
            title: this.dayTitle(lessonSlot.startAt),
            subtitle: lessonSlot.startAt.format('dddd, MMM D'),
            slots: [],
          };
          groups.push(byKey[key]);
        }
        byKey[key].slots.push(lessonSlot);
      });
      return groups;
    },
    activeDates() {
      return this.dayGroups.map((group) => group.key);
    },
    selectedDayGroup() {
      const key = this.selectedDate.format('YYYY-MM-DD');
      return this.dayGroups.find((group) => group.key === key) || null;
    },
    selectedDayEmpty() {
      // The all-empty state below covers the no-slots-at-all case
      if (this.loading || this.dayGroups.length === 0) {
        return false;
      }
      return !this.activeDates.includes(this.selectedDate.format('YYYY-MM-DD'));
    },
    selectedDateInPast() {
      return this.selectedDate.isBefore(moment(), 'day');
    },
    // The nearest loaded day after the selection that has slots, so an empty
    // day offers a one-tap jump instead of a hunt through the strip
    nextOpenDate() {
      const selectedKey = this.selectedDate.format('YYYY-MM-DD');
      const nextKey = this.activeDates.find((key) => key > selectedKey);
      return nextKey ? moment(nextKey, 'YYYY-MM-DD') : null;
    },
    stats() {
      const now = moment();
      const weekOut = moment().add(7, 'days').endOf('day');
      // Anything that hasn't ended yet still counts — an in-progress lesson is
      // still booked; slots only age out of the counter once they're over
      const thisWeek = this.slots.filter((s) => s.endAt.isAfter(now) && s.startAt.isSameOrBefore(weekOut));
      return {
        open: thisWeek.filter((s) => s.status === 'pending').length,
        booked: thisWeek.filter((s) => s.status === 'confirmed').length,
        invited: thisWeek.filter((s) => s.status === 'invited').length,
      };
    },
    addHrefForSelectedDate() {
      return `${this.newAvailabilityPath}?date=${this.selectedDate.format('YYYY-MM-DD')}`;
    },
  },
  mounted() {
    const vm = this;
    vm.refreshAll();
    vm.$notificationManager.on('event-deleted', vm.eventDeleted);
    vm.$notificationManager.on('hide-delete-event-form', vm.hideDeleteForm);
  },
  destroyed() {
    const vm = this;
    vm.$notificationManager.off('event-deleted', vm.eventDeleted);
    vm.$notificationManager.off('hide-delete-event-form', vm.hideDeleteForm);
  },
  methods: {
    dayTitle(dayMoment) {
      if (dayMoment.isSame(moment(), 'day')) {
        return 'Today';
      }
      if (dayMoment.isSame(moment().add(1, 'day'), 'day')) {
        return 'Tomorrow';
      }
      return dayMoment.format('dddd');
    },
    fetchRange(start, end) {
      const params = {
        event_type: 'private_lesson',
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
        coach_ids: [this.coach.id],
        expand: ['event_bookings', 'private_lessons'],
      };
      const url = this.$apiService.eventsUrl(`?${qs.stringify(params, { arrayFormat: 'brackets' })}`);
      return this.$apiService.loadAllPages(url, 'events');
    },
    refreshAll() {
      const vm = this;
      vm.loading = true;
      // From loadedBackTo, not today — a refresh after confirm/decline/delete
      // must not discard days the strip's "Earlier" button already loaded
      vm.fetchRange(vm.loadedBackTo, vm.loadedThrough)
        .then((events) => {
          vm.events = events;
          vm.loading = false;
        })
        .catch((error) => {
          vm.loading = false;
          vm.errorMessage = vm.parseErrorResponse(error);
          vm.error = true;
        });
    },
    loadMore({ startAt, endAt }) {
      const vm = this;
      vm.loadingMore = true;
      vm.fetchRange(startAt, endAt)
        .then((events) => {
          const known = new Set(vm.events.map((e) => e.id));
          vm.events = vm.events.concat(events.filter((e) => !known.has(e.id)));
          if (endAt.isAfter(vm.loadedThrough)) {
            vm.loadedThrough = endAt;
          }
          vm.loadingMore = false;
        })
        .catch((error) => {
          vm.loadingMore = false;
          vm.errorMessage = vm.parseErrorResponse(error);
          vm.error = true;
        });
    },
    // The strip's "Earlier" button walks backwards; past confirmed lessons
    // surface their Check out action once loaded.
    loadEarlier({ startAt, endAt }) {
      const vm = this;
      vm.loadingEarlier = true;
      vm.fetchRange(startAt, endAt)
        .then((events) => {
          const known = new Set(vm.events.map((e) => e.id));
          vm.events = vm.events.concat(events.filter((e) => !known.has(e.id)));
          if (startAt.isBefore(vm.loadedBackTo)) {
            vm.loadedBackTo = startAt;
          }
          vm.loadingEarlier = false;
        })
        .catch((error) => {
          vm.loadingEarlier = false;
          vm.errorMessage = vm.parseErrorResponse(error);
          vm.error = true;
        });
    },
    handleAddClick(e) {
      if (this.forAppEmbed) {
        e.preventDefault();
        this.askAppToPush(`${window.location.origin}${this.addHrefForSelectedDate}`, 'Add Availability');
      }
    },
    confirmDelete(lessonSlot) {
      this.confirmingDeleteSlotId = lessonSlot.id;
    },
    hideDeleteForm() {
      this.confirmingDeleteSlotId = null;
    },
    eventDeleted() {
      // Recurring picks (future/all) can remove siblings we have loaded, so
      // refetch the whole window rather than surgically removing one event
      this.confirmingDeleteSlotId = null;
      this.refreshAll();
    },
    // Coach home confirm/decline. booked_by is the coach whose agenda this is —
    // lesson actions only render on the self-serve home, so that's the actor.
    updateBookingStatus({ lessonSlot, status }) {
      const vm = this;
      vm.updateCoachBookingStatus({
        coachBookingId: lessonSlot.coachBookingId,
        status,
        actorProfileId: vm.coach.id,
        onSuccess() {
          vm.$emit('booking-updated');
          vm.refreshAll();
        },
      });
    },
  },
};
</script>
